/**
 * Security Provider Component
 * Prevents casual copying, right-click, and keyboard shortcuts
 * 
 * ADMINS are EXCLUDED from all restrictions for debugging purposes
 * 
 * Note: These measures deter casual users but are not foolproof
 * against technical users. Always combine with backend security.
 */

import React, { useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const SecurityProvider = ({ children, enabled = true, isAdmin = false }) => {
  
  // If admin, skip all restrictions
  const shouldEnforce = enabled && !isAdmin;
  
  // Blocked keyboard shortcuts
  const blockedKeys = useCallback((e) => {
    if (!shouldEnforce) return;
    
    // Ctrl/Cmd + key combinations
    if (e.ctrlKey || e.metaKey) {
      const blockedCombos = [
        'u', // View source
        's', // Save
        'p', // Print (optional, remove if needed)
        'c', // Copy (in some contexts)
        'a', // Select all
      ];
      
      if (blockedCombos.includes(e.key.toLowerCase())) {
        e.preventDefault();
        toast.warning('Η ενέργεια αυτή δεν επιτρέπεται', {
          description: 'Για λόγους ασφαλείας, η αντιγραφή δεδομένων είναι απενεργοποιημένη.',
          duration: 3000,
        });
        return false;
      }
    }
    
    // Block F12 (DevTools)
    if (e.key === 'F12') {
      e.preventDefault();
      toast.warning('Developer Tools απενεργοποιημένα', {
        duration: 3000,
      });
      return false;
    }
    
    // Block Ctrl+Shift+I (DevTools), Ctrl+Shift+J (Console), Ctrl+Shift+C (Inspector)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
      const blockedShiftCombos = ['i', 'j', 'c'];
      if (blockedShiftCombos.includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
    }
    
    return true;
  }, [shouldEnforce]);
  
  // Block right-click context menu
  const blockContextMenu = useCallback((e) => {
    if (!shouldEnforce) return;
    
    // Allow right-click on input fields for paste functionality
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return true;
    }
    
    e.preventDefault();
    toast.warning('Το δεξί κλικ είναι απενεργοποιημένο', {
      description: 'Για λόγους προστασίας δεδομένων.',
      duration: 2000,
    });
    return false;
  }, [shouldEnforce]);
  
  // Block text selection on sensitive elements
  const blockSelection = useCallback((e) => {
    if (!shouldEnforce) return;
    
    // Allow selection in input fields
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return true;
    }
    
    // Block selection start
    if (e.target.closest('[data-protected="true"]') || 
        e.target.closest('table') || 
        e.target.closest('[data-testid]')) {
      e.preventDefault();
      return false;
    }
  }, [shouldEnforce]);
  
  // Block drag events
  const blockDrag = useCallback((e) => {
    if (!shouldEnforce) return;
    
    // Allow drag in input fields
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return true;
    }
    
    e.preventDefault();
    return false;
  }, [shouldEnforce]);
  
  // Block copy event
  const blockCopy = useCallback((e) => {
    if (!shouldEnforce) return;
    
    // Allow copy in input fields
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return true;
    }
    
    // Check if copying from protected area
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      const parentElement = selection.anchorNode?.parentElement;
      if (parentElement?.closest('[data-protected="true"]') || 
          parentElement?.closest('table')) {
        e.preventDefault();
        toast.warning('Η αντιγραφή δεν επιτρέπεται', {
          description: 'Τα δεδομένα προστατεύονται.',
          duration: 2000,
        });
        return false;
      }
    }
  }, [shouldEnforce]);
  
  // Detect DevTools opening (basic detection) - only for non-admins
  const detectDevTools = useCallback(() => {
    if (!shouldEnforce) return;
    
    const threshold = 160;
    const widthThreshold = window.outerWidth - window.innerWidth > threshold;
    const heightThreshold = window.outerHeight - window.innerHeight > threshold;
    
    if (widthThreshold || heightThreshold) {
      // DevTools might be open - log this event
      console.warn('Security: Possible DevTools detected');
    }
  }, [shouldEnforce]);
  
  useEffect(() => {
    // Add/remove admin-mode class on body for CSS-based protection
    if (isAdmin) {
      document.body.classList.add('admin-mode');
    } else {
      document.body.classList.remove('admin-mode');
    }
    
    if (!shouldEnforce) {
      // Admin mode - no restrictions
      return;
    }
    
    // Add event listeners
    document.addEventListener('keydown', blockedKeys);
    document.addEventListener('contextmenu', blockContextMenu);
    document.addEventListener('selectstart', blockSelection);
    document.addEventListener('dragstart', blockDrag);
    document.addEventListener('copy', blockCopy);
    
    // DevTools detection interval
    const devToolsInterval = setInterval(detectDevTools, 1000);
    
    // Add CSS to prevent selection on body
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    document.body.style.MozUserSelect = 'none';
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', blockedKeys);
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('selectstart', blockSelection);
      document.removeEventListener('dragstart', blockDrag);
      document.removeEventListener('copy', blockCopy);
      clearInterval(devToolsInterval);
      
      // Reset CSS
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.MozUserSelect = '';
    };
  }, [isAdmin, shouldEnforce, blockedKeys, blockContextMenu, blockSelection, blockDrag, blockCopy, detectDevTools]);
  
  return (
    <div 
      data-protected="true"
      style={{ 
        userSelect: shouldEnforce ? 'none' : 'auto',
        WebkitUserSelect: shouldEnforce ? 'none' : 'auto',
      }}
    >
      {children}
    </div>
  );
};

export default SecurityProvider;
